import { createContext, useRef, useState, useEffect, useContext, useMemo } from 'react';

import { useIMLogin } from 'common/hooks/useIMLogin';
import { Image as FuxiImage } from '@fuxi/image-react';
import cx from './index.module.css';
import { getExtFromFileName } from 'common/utils/versionDetail';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import '@fuxi/image-react/style.css';

export type IMDetail = {
  openIM: boolean;
  setOpenIM: (open: boolean) => void;
  unReadMsgCount: number;
  setUnReadMsgCount: (count: number) => void;
};

const NO_PROVIDER = {};
const IMContext = createContext(NO_PROVIDER);
type Props = { shouldIgnoreLogin?: boolean; isManagementBackground?: boolean };
const IMProvider: React.FC<React.PropsWithChildren<Props>> = ({
  shouldIgnoreLogin,
  isManagementBackground,
  children,
}) => {
  const { login } = useIMLogin({ isManagementBackground });
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [openIM, setOpenIM] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unReadMsgCount, setUnReadMsgCount] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [curMsgUuid, setCurMsgUuid] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const chatRoomRef = useRef<HTMLIFrameElement | null>(null);
  const timer = useRef();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isVideo = useMemo(() => {
    const isVideo = getExtFromFileName(previewUrl || '')?.toLowerCase();
    return isVideo === 'mp4' || isVideo === 'mov';
  }, [previewUrl]);

  const onIframeLoad = async () => {
    if (!shouldIgnoreLogin) {
      await login();
    }
  };
  const originalTitle = useRef(document.title);

  const onTitleChange = (hasNew: boolean) => {
    //browser tab高亮
    if (hasNew) {
      if (timer.current) {
        return;
      }
      timer.current = setInterval(() => {
        document.title =
          document.title === originalTitle.current ? '（您有新的消息）' + originalTitle.current : originalTitle.current;
      }, 1000);
    } else {
      clearInterval(timer.current);
      timer.current = null;
      document.title = originalTitle.current;
    }
  };
  useEffect(() => {
    if (unReadMsgCount > 0) {
      onTitleChange(true);
    } else {
      onTitleChange(false);
    }
  }, [unReadMsgCount]);

  //与iframe通信
  useEffect(() => {
    if (!shouldIgnoreLogin) {
      //监听iframe发来的消息
      window.addEventListener('message', (event) => {
        const data = event.data;
        if (data && typeof data === 'string') {
          try {
            console.log('data', data);
            const obj = JSON.parse(data);
            if (obj.type === 'minimize') {
              //iframe隐藏
              setOpenIM(false);
            } else if (obj.type === 'unreadCount') {
              setUnReadMsgCount(parseInt(obj.data));
            } else if (obj.type === 'connectStateChange') {
              //'connecting' | 'connected' | 'disconnected' | 'idle'
              setIsConnected(obj.data !== 'disconnected');
              setIframeKey(Date.now());
              setTimeout(() => {
                login();
              }, 500);
            } else if (obj.type === 'openPreview') {
              console.log('openVideo', obj.data.url);
              setPreviewUrl(obj.data.url);
              setCurMsgUuid(obj.data.msgUuid);
              setHasNext(obj.data.hasNext);
              setHasPrev(obj.data.hasPrev);
            }
          } catch (e) {
            console.log(e);
          }
        }
      });
    }
  }, [shouldIgnoreLogin]);

  useEffect(() => {
    if (openIM) {
      chatRoomRef.current?.contentWindow?.postMessage(JSON.stringify({ type: 'open' }), '*');
    } else {
      chatRoomRef.current?.contentWindow?.postMessage(JSON.stringify({ type: 'close' }), '*');
    }
  }, [openIM]);
  const prevArtwork = () => {
    chatRoomRef.current?.contentWindow?.postMessage(
      JSON.stringify({ type: 'prevPreview', data: { msgUuid: curMsgUuid } }),
      '*',
    );
  };
  const nextArtwork = () => {
    chatRoomRef.current?.contentWindow?.postMessage(
      JSON.stringify({ type: 'nextPreview', data: { msgUuid: curMsgUuid } }),
      '*',
    );
  };
  const onPreviewClose = () => {
    setPreviewUrl(null);
    setCurMsgUuid(null);
    setHasNext(false);
    setHasPrev(false);
    chatRoomRef.current?.contentWindow?.postMessage(JSON.stringify({ type: 'closePreview' }), '*');
  };

  return (
    <IMContext.Provider value={{ openIM, setOpenIM, unReadMsgCount, setUnReadMsgCount }}>
      <iframe
        key={iframeKey}
        title="chatRoom"
        onLoad={onIframeLoad}
        id="chatRoom"
        ref={chatRoomRef}
        src="/im-chatroom"
        // src="https://fuxi-art-platform-external.apps-sl.danlu.netease.com/im-chatroom"
        className={cx('chat-room', openIM && 'chat-room-show')}></iframe>
      {children}
      {previewUrl && (
        <div className={cx('drawer-mask')}>
          <div className={cx('drawer-content-header')} onClick={onPreviewClose}>
            <CloseOutlined className="font-size-heading-3 mr-sm mb-sm" />
          </div>

          <div className={cx('preview-image-wrapper')}>
            {!isVideo ? (
              <FuxiImage src={previewUrl as string} alt="preview" movable={true} operation={true} showScale={true} />
            ) : (
              <video className="w-100% h-100% object-contain" src={previewUrl as string} controls={true} />
            )}
          </div>
          <div onClick={prevArtwork} className={cx('arrow-wrapper', 'arrow-left', { 'arrow-disabled': !hasPrev })}>
            <LeftOutlined />
          </div>
          <div onClick={nextArtwork} className={cx('arrow-wrapper', 'arrow-right', { 'arrow-disabled': !hasNext })}>
            <RightOutlined />
          </div>
        </div>
      )}
    </IMContext.Provider>
  );
};
const useIMContext = () => {
  const value = useContext(IMContext);

  if (value === NO_PROVIDER) {
    throw new Error('useIMContext must be used within a IMProvider');
  }
  return value as IMDetail;
};
export { IMProvider, useIMContext };
